import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */


console.log("media gellery file chal rahi hai");

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Listen for variant changes from color swatches and other sources
    document.addEventListener('variant:change', this.#handleVariantChange, { signal });
    document.addEventListener('variantChange', this.#handleVariantChange, { signal });
    document.addEventListener('product:variant-change', this.#handleVariantChange, { signal });
    
    // Listen for form changes that might indicate variant change
    const productForm = document.querySelector('[data-product-form]');
    if (productForm) {
      productForm.addEventListener('change', this.#handleFormChange, { signal });
    }

    // Listen for color swatch changes directly
    document.addEventListener('change', this.#handleColorSwatchChange, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Store the variant's featured media ID and presentation type before replacing
    const variantResource = event.detail.resource;
    const featuredMediaId = variantResource?.featured_media?.id;
    const presentation = this.presentation || this.dataset.presentation;

    this.replaceWith(newMediaGallery);

    // After replacement, handle the update based on presentation type
    if (featuredMediaId) {
      requestAnimationFrame(() => {
        const updatedGallery = document.querySelector('media-gallery');
        if (!updatedGallery) return;

        // Handle grid layouts (one or two columns)
        if (presentation === 'grid') {
          this.#handleGridVariantUpdate(updatedGallery, featuredMediaId);
        } else {
          // Handle carousel/slideshow layouts
          this.#handleSlideshowVariantUpdate(updatedGallery, featuredMediaId);
        }
      });
    }
  };

  /**
   * Handles variant change events from various sources (color swatches, etc.)
   * Updates the grid order without full page reload.
   *
   * @param {Event} event - The variant change event.
   */
  #handleVariantChange = (event) => {
    const variantId = event.detail?.id || event.detail?.variant?.id || event.detail?.selectedVariant?.id;
    if (!variantId) return;

    // Get variant data from the page
    let variant = null;
    
    // Try to get variant data from script tag
    const variantScript = document.querySelector('script[type="application/json"][data-variants]');
    if (variantScript) {
      try {
        const variants = JSON.parse(variantScript.textContent);
        variant = variants.find(v => String(v.id) === String(variantId));
      } catch (e) {
        console.warn('Failed to parse variant data from script:', e);
      }
    }

    // If not found, try to get from data attributes on variant inputs
    if (!variant) {
      const variantInput = document.querySelector(`input[data-variant-id="${CSS.escape(String(variantId))}"]`);
      if (variantInput) {
        // Try to get featured media ID from data attributes
        const featuredMediaId = variantInput.getAttribute('data-featured-media-id') ||
                                variantInput.closest('[data-featured-media-id]')?.getAttribute('data-featured-media-id');
        
        if (featuredMediaId) {
          const presentation = this.presentation || this.dataset.presentation;
          if (presentation === 'grid') {
            this.#handleGridVariantUpdate(this, featuredMediaId);
          } else {
            this.#handleSlideshowVariantUpdate(this, featuredMediaId);
          }
          return;
        }
      }
    }

    // Use variant data if found
    if (variant && variant.featured_media?.id) {
      const featuredMediaId = variant.featured_media.id;
      const presentation = this.presentation || this.dataset.presentation;

      // Handle grid layouts without page reload
      if (presentation === 'grid') {
        this.#handleGridVariantUpdate(this, featuredMediaId);
      } else {
        this.#handleSlideshowVariantUpdate(this, featuredMediaId);
      }
    }
  };

  /**
   * Handles form change events that might indicate variant selection.
   *
   * @param {Event} event - The form change event.
   */
  #handleFormChange = (event) => {
    const target = event.target;
    if (target.name === 'id' || target.classList.contains('product-variant-id') || target.hasAttribute('data-variant-id')) {
      const variantId = target.value || target.getAttribute('data-variant-id');
      if (variantId) {
        // Trigger variant change handling
        this.#handleVariantChange({ detail: { id: variantId } });
      }
    }
  };

  /**
   * Handles color swatch change events.
   *
   * @param {Event} event - The change event.
   */
  #handleColorSwatchChange = (event) => {
    const target = event.target;
    
    // Check if this is a color swatch input
    if (target.classList.contains('option-selector-input') && target.name?.includes('color')) {
      // Find the variant ID associated with this color
      const variantId = target.getAttribute('data-variant-id') || 
                       target.closest('[data-variant-id]')?.getAttribute('data-variant-id');
      
      if (variantId) {
        // Small delay to ensure variant data is updated
        setTimeout(() => {
          this.#handleVariantChange({ detail: { id: variantId } });
        }, 50);
      } else {
        // Try to find variant from form input
        const formInput = document.querySelector('[name="id"], .product-variant-id');
        if (formInput && formInput.value) {
          setTimeout(() => {
            this.#handleVariantChange({ detail: { id: formInput.value } });
          }, 50);
        }
      }
    }
  };

  /**
   * Handles variant update for grid layouts (one or two columns).
   * Ensures the variant's featured image is first in the grid and visible.
   *
   * @param {HTMLElement} gallery - The updated media gallery element.
   * @param {string|number} featuredMediaId - The featured media ID of the selected variant.
   */
  #handleGridVariantUpdate(gallery, featuredMediaId) {
    const grid = gallery.querySelector('.media-gallery__grid');
    if (!grid) return;

    // Find the media item that matches the variant's featured image
    const mediaItems = Array.from(grid.querySelectorAll('li'));
    const variantMediaItem = mediaItems.find(item => {
      const mediaElement = item.querySelector('[data-media-id]');
      return mediaElement && mediaElement.getAttribute('data-media-id') === String(featuredMediaId);
    });

    // If we found the variant's media item and it's not already first, move it to the first position
    if (variantMediaItem && variantMediaItem !== mediaItems[0]) {
      grid.insertBefore(variantMediaItem, grid.firstChild);
    }

    // Also update slideshow if present (used for mobile carousel in grid mode)
    const slideshow = gallery.querySelector('slideshow-component');
    if (slideshow && slideshow.select) {
      const slide = slideshow.querySelector(`slideshow-slide[slide-id="${CSS.escape(String(featuredMediaId))}"]`);
      if (slide) {
        slideshow.select({ id: String(featuredMediaId) }, undefined, { animate: false });
      }
    }

    // Ensure the first item exists, but do not force page scroll on variant change
const firstMediaItem = grid.querySelector('li:first-child');
if (!firstMediaItem) return;

// Do nothing here.
// We only reorder media/select the correct image and intentionally prevent
// automatic scroll/jump on laptop and desktop screens.
  }

  /**
   * Handles variant update for carousel/slideshow layouts.
   * Selects the correct slide based on the variant's featured media.
   *
   * @param {HTMLElement} gallery - The updated media gallery element.
   * @param {string|number} featuredMediaId - The featured media ID of the selected variant.
   */
  #handleSlideshowVariantUpdate(gallery, featuredMediaId) {
    let retryCount = 0;
    const maxRetries = 20; // Maximum 1 second (20 * 50ms)
    const waitForSlideshow = () => {
      const slideshow = gallery.querySelector('slideshow-component');
      if (slideshow && slideshow.select) {
        // Try to find slide by media ID (slide-id attribute)
        const slide = slideshow.querySelector(`slideshow-slide[slide-id="${CSS.escape(String(featuredMediaId))}"]`);
        if (slide) {
          slideshow.select({ id: String(featuredMediaId) }, undefined, { animate: false });
        } else {
          // Fallback: find by data-media-id
          const mediaElement = slideshow.querySelector(`[data-media-id="${CSS.escape(String(featuredMediaId))}"]`);
          if (mediaElement) {
            const parentSlide = mediaElement.closest('slideshow-slide');
            if (parentSlide) {
              const slideId = parentSlide.getAttribute('slide-id');
              if (slideId) {
                slideshow.select({ id: slideId }, undefined, { animate: false });
              }
            }
          }
        }
      } else if (retryCount < maxRetries) {
        // Slideshow not ready yet, try again
        retryCount++;
        setTimeout(waitForSlideshow, 50);
      }
    };
    waitForSlideshow();
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
